import { useState } from "react";
import { Link } from "react-router-dom";
import { Download, Search } from "lucide-react";
import { API, useFetch } from "../lib/api";
import { Page, Spinner, ErrorBox, EmptyBox, StatusBadge, Badge, Pager, REL, fmt } from "./ui";

const SELECTS = [
  ["status", "Statut", [["active", "Actif"], ["suspended", "Suspendu"]]],
  ["onboarding", "Démarrage", [["complete", "Terminé"], ["incomplete", "Incomplet"]]],
  ["activity", "Activité", [["active", "Active"], ["inactive", "Inactive"], ["never", "Jamais"]]],
  ["relation", "Relation AVELIS", Object.entries(REL)],
];

function Row({ a }) {
  const ob = a.onboarding;
  return (
    <Link to={`/admin/comptes/${a.id}`} className="grid gap-1 px-4 py-3 text-sm transition-colors hover:bg-slate-50 md:grid-cols-[2fr_1.6fr_.9fr_.8fr_.7fr_1fr_.9fr_.6fr_1.1fr] md:items-center md:gap-3" data-testid="account-row">
      <div className="font-bold text-[#132F3A]">{a.name}</div>
      <div className="min-w-0"><div className="truncate">{a.owner_name}</div><div className="truncate text-xs text-slate-500">{a.owner_email}</div></div>
      <div className="text-slate-600"><span className="md:hidden text-slate-400">Inscription : </span>{fmt(a.created_at)}</div>
      <div><StatusBadge status={a.status} /></div>
      <div className="text-slate-600"><span className="md:hidden text-slate-400">Membres : </span>{a.members}{a.invites_pending ? <span className="text-xs text-amber-700"> (+{a.invites_pending})</span> : ""}</div>
      <div className="text-slate-600"><span className="md:hidden text-slate-400">Dernière activité : </span>{a.last_activity ? fmt(a.last_activity) : "Aucune"}</div>
      <div><Badge tone={ob.complete ? "green" : "amber"}>{ob.done}/{ob.total}</Badge></div>
      <div>{a.tickets_open ? <Badge tone="red">{a.tickets_open}</Badge> : <span className="text-slate-300">0</span>}</div>
      <div><Badge tone={a.relation === "client" ? "teal" : "slate"}>{REL[a.relation]}</Badge></div>
    </Link>
  );
}

export default function Accounts() {
  const [f, setF] = useState({ q: "", status: "", onboarding: "", activity: "", relation: "", sort: "created_desc" });
  const [page, setPage] = useState(1);
  const qs = new URLSearchParams({ ...f, page }).toString();
  const { data, error } = useFetch(`/admin/accounts?${qs}`);
  const set = (k) => (e) => { setPage(1); setF({ ...f, [k]: e.target.value }); };
  const csv = `${API}/admin/accounts.csv?${new URLSearchParams({ ...f }).toString()}`;
  return (
    <Page title="Comptes et équipes" subtitle="Un compte = une équipe cliente (ou une infirmière seule)." testid="admin-accounts"
      actions={<a href={csv} className="btn-ghost !min-h-[42px] text-sm" data-testid="accounts-export-csv"><Download className="h-4 w-4" /> Export CSV</a>}>
      <div className="card mb-4 grid gap-2 p-3 md:grid-cols-6">
        <label className="relative md:col-span-2"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input className="field !min-h-[42px] !pl-9" placeholder="Équipe, responsable ou e-mail" value={f.q} onChange={set("q")} data-testid="accounts-search" /></label>
        {SELECTS.map(([k, l, opts]) => (
          <select key={k} className="field !min-h-[42px]" value={f[k]} onChange={set(k)} data-testid={`accounts-filter-${k}`}>
            <option value="">{l} : tous</option>
            {opts.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        ))}
        <select className="field !min-h-[42px] md:col-span-2" value={f.sort} onChange={set("sort")} data-testid="accounts-sort">
          <option value="created_desc">Inscription (récentes)</option><option value="created_asc">Inscription (anciennes)</option>
          <option value="activity_desc">Dernière activité (récente)</option><option value="activity_asc">Dernière activité (ancienne)</option>
        </select>
      </div>
      {error ? <ErrorBox error={error} /> : !data ? <Spinner /> : data.items.length === 0 ? <EmptyBox text="Aucun compte ne correspond à ces critères." /> : (
        <>
          <div className="mb-2 text-sm text-slate-500" data-testid="accounts-total">{data.total} compte(s)</div>
          <div className="card divide-y divide-slate-100 overflow-hidden">
            <div className="hidden bg-slate-50 px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 md:grid md:grid-cols-[2fr_1.6fr_.9fr_.8fr_.7fr_1fr_.9fr_.6fr_1.1fr] md:gap-3">
              <span>Compte</span><span>Responsable</span><span>Inscription</span><span>Statut</span><span>Membres</span><span>Dernière activité</span><span>Démarrage</span><span>Assist.</span><span>Relation</span>
            </div>
            {data.items.map((a) => <Row key={a.id} a={a} />)}
          </div>
          <Pager page={page} total={data.total} per={data.per_page} setPage={setPage} />
        </>
      )}
    </Page>
  );
}
